import { useEffect, useId, useRef, useState } from "react";
import {
  formatBoothHit,
  searchFloorBooths,
  type BoothSearchHit,
} from "../floor/boothLookup";
import { callTool } from "../api";
import { SearchClearButton } from "./SearchTile";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function BoothSearchField({
  id,
  eventKey,
  campaignKey,
  value,
  onChange,
  onPick,
  placeholder = "Account or booth",
  toolCaller = callTool,
}: {
  id: string;
  eventKey: string;
  campaignKey?: string;
  value: string;
  onChange: (value: string) => void;
  onPick: (hit: BoothSearchHit) => void;
  placeholder?: string;
  toolCaller?: typeof callTool;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const requestGen = useRef(0);
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<BoothSearchHit[]>([]);
  const [pending, setPending] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebouncedValue(value.trim(), 250);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
  }, [open]);

  useEffect(() => {
    if (!debounced) {
      requestGen.current += 1;
      setHits([]);
      setPending(false);
      return;
    }
    const gen = ++requestGen.current;
    setPending(true);
    void searchFloorBooths(eventKey, debounced, campaignKey, toolCaller)
      .then((rows) => {
        if (gen !== requestGen.current) return;
        setHits(rows);
        setActiveIndex(0);
      })
      .finally(() => {
        if (gen === requestGen.current) setPending(false);
      });
  }, [debounced, eventKey, campaignKey]);

  function pick(hit: BoothSearchHit) {
    onChange(formatBoothHit(hit));
    onPick(hit);
    setOpen(false);
  }

  const showMenu = open && Boolean(value.trim());

  return (
    <div className="booth-search" ref={rootRef}>
      <input
        id={id}
        type="search"
        role="combobox"
        aria-expanded={showMenu}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showMenu && hits[activeIndex] ? `${listId}-${hits[activeIndex].booth}` : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.min(index + 1, Math.max(hits.length - 1, 0)));
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === "Enter" && showMenu && hits.length) {
            event.preventDefault();
            pick(hits[activeIndex] ?? hits[0]);
          } else if (event.key === "Escape") {
            if (open) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            }
          }
        }}
      />
      {value ? (
        <SearchClearButton
          onClear={() => {
            onChange("");
            setOpen(false);
            setHits([]);
            setPending(false);
          }}
        />
      ) : null}
      {showMenu ? (
        <ul className="booth-search-menu" id={listId} role="listbox">
          {pending && !hits.length ? <li className="booth-search-status">Searching…</li> : null}
          {!pending && !hits.length ? <li className="booth-search-status">No matching account or booth</li> : null}
          {hits.map((hit, index) => (
            <li key={hit.booth} role="presentation">
              <button
                type="button"
                id={`${listId}-${hit.booth}`}
                role="option"
                className="booth-search-option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(hit)}
              >
                <strong>{hit.label}</strong>
                <span className="muted">
                  Booth {hit.booth}
                  {hit.hall ? ` · Hall ${hit.hall}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
